import React, { useState } from 'react';
import {
  Card,
  Typography,
  Button,
  Select,
  Empty,
  Avatar,
  Tag,
  Popconfirm,
  Upload,
  Input,
  Modal,
  message,
  Tooltip,
  Space,
  Spin,
} from 'antd';
import { FileOutlined, DownloadOutlined, UploadOutlined, EditOutlined, DeleteOutlined, EyeOutlined, FilePdfOutlined, FileImageOutlined } from '@ant-design/icons';
import { actionApi } from '../../../utils/actionApi';
import { getSuccessMessage } from '../../../utils/actionMessageTranslator';
import { CardListSkeleton } from '../../../components/SkeletonLoaders';

const { Title, Text } = Typography;
const { TextArea } = Input;

export default function ActionAttachmentsTab({
  actionId,
  attachments,
  attachmentsLoading,
  attachmentContext,
  setAttachmentContext,
  attachmentDescription,
  setAttachmentDescription,
  attachmentUploading,
  setAttachmentUploading,
  attachmentFileList,
  setAttachmentFileList,
  editingAttachmentId,
  setEditingAttachmentId,
  editingAttachmentDescription,
  setEditingAttachmentDescription,
  editingAttachmentLoading,
  loadAttachments,
  primaryButtonClass,
}) {
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  return (
    <Card style={{ borderRadius: 16 }} bodyStyle={{ padding: 16 }}>
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-4">
        <div>
          <Title level={5} style={{ margin: 0, fontFamily: 'CustomFont' }}>
            پیوست‌های اقدام
          </Title>
          {/* <Text type="secondary" style={{ fontFamily: 'CustomFont' }}>
            آپلود و مدیریت فایل‌های مرتبط با این اقدام
          </Text> */}
        </div>
        {/* <Select
          size="middle"
          value={attachmentContext}
          onChange={(val) => {
            setAttachmentContext(val);
            setTimeout(() => {
              loadAttachments();
            }, 0);
          }}
          style={{ minWidth: 140, borderRadius: 10, fontFamily: 'CustomFont' }}
          options={[
            { value: 'default', label: 'Default' },
            { value: 'timeline', label: 'Timeline' },
          ]}
        /> */}
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          {attachmentsLoading ? (
            <CardListSkeleton count={3} />
          ) : attachments.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span style={{ fontFamily: 'CustomFont' }}>
                  هنوز پیوستی ثبت نشده است
                </span>
              }
            />
          ) : (
            <div className="space-y-3">
              {attachments.map((att) => {
                // استفاده از uploaded_at یا created_at که به صورت رشته فارسی است
                const dateTimeStr = att.uploaded_at || att.created_at || '';
                const [dateStr, timeStr] = dateTimeStr ? dateTimeStr.split(' | ') : ['', ''];
                
                // تشخیص نوع فایل برای آیکون
                const isPdf = att.mime_type?.includes('pdf') || att.file_name?.toLowerCase().endsWith('.pdf');
                const isImage = att.mime_type?.startsWith('image/') || 
                  /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(att.file_name || '');
                
                const fileIcon = isPdf ? (
                  <FilePdfOutlined style={{ fontSize: 18 }} />
                ) : isImage ? (
                  <FileImageOutlined style={{ fontSize: 18 }} />
                ) : (
                  <FileOutlined style={{ fontSize: 18 }} />
                );
                
                const iconBgColor = isPdf 
                  ? 'bg-red-50 dark:bg-red-900/20 text-red-500'
                  : isImage 
                  ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-500'
                  : 'bg-blue-50 dark:bg-blue-900/20 text-blue-500';
                
                return (
                  <div
                    key={att.id}
                    className="p-4 rounded-xl border border-gray-200 dark:border-gray-800 flex flex-col gap-3 hover:shadow-sm transition-shadow bg-white dark:bg-gray-900"
                  >
                    <div className="flex items-start justify-between gap-4">
                      {/* سمت چپ: آیکون و نام فایل */}
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className={`w-10 h-10 rounded-xl ${iconBgColor} flex items-center justify-center flex-shrink-0`}>
                          {fileIcon}
                        </div>
                        <div className="flex flex-col gap-1 flex-1 min-w-0">
                          <Text 
                            style={{ fontFamily: 'CustomFont', fontWeight: 500 }}
                            className="truncate"
                          >
                            {att.file_name}
                          </Text>
                          <Text
                            type="secondary"
                            style={{ fontFamily: 'CustomFont', fontSize: 11 }}
                          >
                            {(att.file_size / 1024 / 1024).toFixed(2)} MB
                            {/* {att.context ? ` • ${att.context}` : ''} */}
                          </Text>
                        </div>
                      </div>

                      {/* سمت راست: تاریخ و دکمه‌ها */}
                      <div className="flex items-start gap-3 flex-shrink-0">
                        {/* تاریخ و ساعت */}
                        {dateTimeStr && (
                          <div className="flex flex-col items-end gap-0.5">
                            <Text
                              type="secondary"
                              style={{ fontFamily: 'CustomFont', fontSize: 11 }}
                            >
                              {dateStr}
                            </Text>
                            {timeStr && (
                              <Text
                                type="secondary"
                                style={{ fontFamily: 'CustomFont', fontSize: 10 }}
                              >
                                {timeStr}
                              </Text>
                            )}
                          </div>
                        )}

                        {/* دکمه‌های عملیات */}
                        <Space size="small">
                          <Tooltip title="پیش‌نمایش">
                            <Button
                              type="text"
                              size="small"
                              icon={<EyeOutlined />}
                              style={{ fontFamily: 'CustomFont' }}
                              onClick={async () => {
                                try {
                                  setPreviewLoading(true);
                                  setPreviewAttachment(att);
                                  const blob = await actionApi.downloadActionAttachment(actionId, att.id);
                                  const blobUrl = window.URL.createObjectURL(blob);
                                  setPreviewBlobUrl(blobUrl);
                                  setPreviewModalVisible(true);
                                  setPreviewLoading(false);
                                } catch (e) {
                                  console.error(e);
                                  message.error('خطا در باز کردن پیش‌نمایش فایل');
                                  setPreviewLoading(false);
                                }
                              }}
                            />
                          </Tooltip>
                          <Tooltip title="دانلود">
                            <Button
                              type="text"
                              size="small"
                              icon={<DownloadOutlined />}
                              style={{ fontFamily: 'CustomFont' }}
                              onClick={async () => {
                                try {
                                  const blob = await actionApi.downloadActionAttachment(actionId, att.id);
                                  const url = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = att.file_name || 'attachment';
                                  document.body.appendChild(a);
                                  a.click();
                                  a.remove();
                                  window.URL.revokeObjectURL(url);
                                } catch (e) {
                                  console.error(e);
                                  message.error('خطا در دانلود فایل');
                                }
                              }}
                            />
                          </Tooltip>
                          <Tooltip title="ویرایش توضیحات">
                            <Button
                              type="text"
                              size="small"
                              icon={<EditOutlined />}
                              style={{ fontFamily: 'CustomFont' }}
                              onClick={() => {
                                setEditingAttachmentId(att.id);
                                setEditingAttachmentDescription(att.description || '');
                              }}
                            />
                          </Tooltip>
                          <Popconfirm
                            title="حذف پیوست"
                            description="آیا از حذف این پیوست مطمئن هستید؟"
                            okText="حذف"
                            cancelText="انصراف"
                            okButtonProps={{ danger: true }}
                            onConfirm={async () => {
                              try {
                                const res = await actionApi.deleteActionAttachment(actionId, att.id);
                                if (res?.success) {
                                  message.success(getSuccessMessage(res.message, 'پیوست حذف شد'));
                                  await loadAttachments();
                                } else {
                                  message.error(res?.message || 'خطا در حذف پیوست');
                                }
                              } catch (e) {
                                console.error(e);
                                message.error(e.message || 'خطا در حذف پیوست');
                              }
                            }}
                          >
                            <Tooltip title="حذف">
                              <Button
                                type="text"
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                style={{ fontFamily: 'CustomFont' }}
                              />
                            </Tooltip>
                          </Popconfirm>
                        </Space>
                      </div>
                    </div>

                    {/* توضیحات */}
                    {att.description && (
                      <Text
                        style={{ fontFamily: 'CustomFont', fontSize: 12 }}
                        className="text-gray-700 dark:text-gray-300"
                      >
                        {att.description}
                      </Text>
                    )}

                    {/* آپلودکننده */}
                    {att.uploader && (
                      <div className="flex items-center gap-2">
                        <Avatar size="small">
                          {String(att.uploader?.name || '').slice(0, 1) || '?'}
                        </Avatar>
                        <Text
                          type="secondary"
                          style={{ fontFamily: 'CustomFont', fontSize: 11 }}
                        >
                          {att.uploader?.name || 'کاربر ناشناس'}
                        </Text>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="lg:col-span-1">
          <Card
            size="small"
            style={{ borderRadius: 16, background: '#f9fafb' }}
            bodyStyle={{ padding: 12 }}
          >
            <Title level={5} style={{ margin: 0, fontFamily: 'CustomFont' }}>
              آپلود پیوست جدید
            </Title>
            <Text type="secondary" style={{ fontFamily: 'CustomFont', fontSize: 12 }}>
              فایل را انتخاب کنید و در صورت نیاز توضیح و context را تنظیم کنید.
            </Text>

            <div className="mt-3 space-y-3">
              <Upload.Dragger
                fileList={attachmentFileList}
                beforeUpload={(file) => {
                  setAttachmentFileList([file]);
                  return false;
                }}
                onRemove={() => {
                  setAttachmentFileList([]);
                }}
                multiple={false}
                style={{
                  borderRadius: 12,
                  padding: '20px',
                  background: '#fff',
                  border: '2px dashed #d1d5db',
                }}
                className="dark:bg-gray-800 dark:border-gray-700"
              >
                <p className="ant-upload-drag-icon" style={{ marginBottom: 8 }}>
                  <UploadOutlined style={{ fontSize: 32, color: '#9ca3af' }} />
                </p>
                <p className="ant-upload-text" style={{ fontFamily: 'CustomFont', color: '#374151', marginBottom: 4 }}>
                  فایل را اینجا بکشید یا کلیک کنید
                </p>
                <p className="ant-upload-hint" style={{ fontFamily: 'CustomFont', fontSize: 12, color: '#6b7280' }}>
                  فایل را انتخاب کنید یا اینجا بکشید
                </p>
              </Upload.Dragger>

              <div>
                <Text style={{ fontFamily: 'CustomFont' }}>
                  توضیحات فایل (اختیاری)
                </Text>
                <TextArea
                  rows={3}
                  maxLength={500}
                  value={attachmentDescription}
                  onChange={(e) => setAttachmentDescription(e.target.value)}
                  style={{ marginTop: 4, borderRadius: 10 }}
                />
              </div>

              {/* <div>
                <Text style={{ fontFamily: 'CustomFont' }}>
                  context نمایش
                </Text>
                <Select
                  size="middle"
                  value={attachmentContext}
                  onChange={setAttachmentContext}
                  style={{ width: '100%', marginTop: 4 }}
                  options={[
                    { value: 'default', label: 'Default' },
                    { value: 'timeline', label: 'Timeline' },
                  ]}
                />
              </div> */}

              <div className="flex justify-end">
                <Button
                  type="primary"
                  className={primaryButtonClass}
                  loading={attachmentUploading}
                  disabled={attachmentUploading}
                  style={{ borderRadius: 12, fontFamily: 'CustomFont' }}
                  onClick={async () => {
                    if (!attachmentFileList.length) {
                      message.error('ابتدا یک فایل انتخاب کنید');
                      return;
                    }
                    const file = attachmentFileList[0];
                    setAttachmentUploading(true);
                    try {
                      const res = await actionApi.uploadActionAttachment(actionId, {
                        file,
                        description: attachmentDescription || undefined,
                        context: attachmentContext || undefined,
                      });
                      if (res?.success) {
                        message.success(getSuccessMessage(res.message, 'فایل با موفقیت بارگذاری شد'));
                        setAttachmentFileList([]);
                        setAttachmentDescription('');
                        await loadAttachments();
                      } else {
                        message.error(res?.message || 'خطا در بارگذاری فایل');
                      }
                    } catch (e) {
                      console.error(e);
                      message.error(e.message || 'خطا در بارگذاری فایل');
                    } finally {
                      setAttachmentUploading(false);
                    }
                  }}
                >
                  آپلود فایل
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <Modal
        title={
          <span style={{ fontFamily: 'CustomFont' }}>
            ویرایش توضیحات پیوست
          </span>
        }
        open={!!editingAttachmentId}
        onCancel={() => {
          setEditingAttachmentId(null);
          setEditingAttachmentDescription('');
        }}
        onOk={async () => {
          if (!editingAttachmentId) return;
          try {
            const res = await actionApi.updateActionAttachmentDescription(
              actionId,
              editingAttachmentId,
              editingAttachmentDescription || null
            );
            if (res?.success) {
              message.success(getSuccessMessage(res.message, 'توضیحات پیوست به‌روزرسانی شد'));
              setEditingAttachmentId(null);
              setEditingAttachmentDescription('');
              await loadAttachments();
            } else {
              message.error(res?.message || 'خطا در به‌روزرسانی توضیحات');
            }
          } catch (e) {
            console.error(e);
            message.error(e.message || 'خطا در به‌روزرسانی توضیحات');
          }
        }}
        okText="ذخیره"
        cancelText="انصراف"
        confirmLoading={editingAttachmentLoading}
      >
        <TextArea
          rows={4}
          value={editingAttachmentDescription}
          onChange={(e) => setEditingAttachmentDescription(e.target.value)}
          maxLength={500}
          style={{ borderRadius: 10, fontFamily: 'CustomFont' }}
        />
      </Modal>

      {/* Modal پیش‌نمایش فایل */}
      <Modal
        title={
          <span style={{ fontFamily: 'CustomFont' }}>
            پیش‌نمایش: {previewAttachment?.file_name || ''}
          </span>
        }
        open={previewModalVisible}
        onCancel={() => {
          setPreviewModalVisible(false);
          if (previewBlobUrl) {
            window.URL.revokeObjectURL(previewBlobUrl);
            setPreviewBlobUrl(null);
          }
          setPreviewAttachment(null);
        }}
        footer={null}
        width="90%"
        style={{ maxWidth: 1200 }}
        bodyStyle={{ padding: 0, minHeight: 500 }}
      >
        {previewLoading ? (
          <div className="flex items-center justify-center" style={{ minHeight: 500 }}>
            <Spin size="large" />
          </div>
        ) : previewBlobUrl && previewAttachment ? (
          <div className="w-full" style={{ minHeight: 500 }}>
            {(() => {
              const isPdf = previewAttachment.mime_type?.includes('pdf') || 
                previewAttachment.file_name?.toLowerCase().endsWith('.pdf');
              const isImage = previewAttachment.mime_type?.startsWith('image/') || 
                /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(previewAttachment.file_name || '');

              if (isPdf) {
                return (
                  <iframe
                    src={previewBlobUrl}
                    style={{
                      width: '100%',
                      height: '80vh',
                      border: 'none',
                      borderRadius: 8,
                    }}
                    title="PDF Preview"
                  />
                );
              } else if (isImage) {
                return (
                  <div className="flex items-center justify-center p-4" style={{ minHeight: 500 }}>
                    <img
                      src={previewBlobUrl}
                      alt={previewAttachment.file_name}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '80vh',
                        objectFit: 'contain',
                        borderRadius: 8,
                      }}
                    />
                  </div>
                );
              } else {
                return (
                  <div className="flex flex-col items-center justify-center p-8" style={{ minHeight: 500 }}>
                    <FileOutlined style={{ fontSize: 64, color: '#9ca3af', marginBottom: 16 }} />
                    <Text style={{ fontFamily: 'CustomFont', fontSize: 16, color: '#6b7280', marginBottom: 8 }}>
                      پیش‌نمایش برای این نوع فایل در دسترس نیست
                    </Text>
                    <Text style={{ fontFamily: 'CustomFont', fontSize: 14, color: '#9ca3af' }}>
                      لطفاً فایل را دانلود کنید
                    </Text>
                  </div>
                );
              }
            })()}
          </div>
        ) : null}
      </Modal>
    </Card>
  );
}
